import type {ChangeEvent} from 'react';
import React from 'react';

import styled from '@emotion/styled';
import {Color} from '@nfq/colors';
import {media, spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';
import * as motion from 'motion/react-m';

import {P} from 'UI/components/layout/Text';

import {FadeInAnimation} from 'UI/animations/shared';

import {useFormCheckboxInput} from './useFormCheckboxInput';
import {Check} from 'Images/icons';
import {iconAsBackground} from 'UI/utils/helpers';

import type {ValidationFunc} from 'Shared/formHelperTypes';
import type {WithChildren} from 'types/global';

/**
 * The ComponentProps interface defines the properties for the FormCheckboxInput component.
 * This interface specifies all the configuration options and event handlers needed to create a fully functional checkbox input with validation support.
 * It includes properties for controlling the checkbox state, styling, accessibility, and form integration capabilities.
 */
interface ComponentProps {
    /**
     * Optional CSS class name to apply additional styling to the wrapper element of the checkbox input component.
     */
    className?: string;
    /**
     * The initial checked state of the checkbox when the component first renders.
     */
    initialValue: boolean;
    /**
     * Whether the checkbox input is disabled and non-interactive, preventing user interaction when set to true.
     *
     * @default false
     */
    isDisabled?: boolean;
    /**
     * Whether the checkbox input is required for form submission, adding validation constraints when set to true.
     *
     * @default false
     */
    isRequired?: boolean;
    /**
     * The unique name identifier for the checkbox input field, used for form data collection and accessibility.
     */
    name: string;
    /**
     * Callback function that executes when the checkbox value changes, receiving the change event as a parameter.
     */
    onChange?(e: ChangeEvent<HTMLInputElement>): void;
    /**
     * Whether the checkbox selection box should be vertically centered with the label content instead of aligned to the top.
     *
     * @default false
     */
    selectionBoxIsCentered?: boolean;
    /**
     * The test identifier for component testing purposes, used to uniquely identify this component during automated testing.
     *
     * @default 'FormCheckboxInput'
     */
    testId?: string;
    /**
     * The validation function to validate the checkbox input value, returning an error message string or null if valid.
     */
    validation?: ValidationFunc | undefined;
}

/**
 * The FormCheckboxInput component renders a customizable checkbox input field with validation support and error handling.
 * This component is wrapped with MobX observer to reactively update when the underlying form state changes.
 * It provides a styled checkbox interface with support for labels, error states, and custom positioning options.
 *
 * @param props                        The component props.
 * @param props.children               The content to display as the checkbox label.
 * @param props.initialValue           The initial checked state of the checkbox.
 * @param props.isDisabled             Whether the checkbox input is disabled and non-interactive.
 * @param props.isRequired             Whether the checkbox input is required for form submission.
 * @param props.name                   The unique name identifier for the checkbox input field.
 * @param props.onChange               Callback function that executes when the checkbox value changes.
 * @param props.selectionBoxIsCentered Whether the checkbox selection box should be vertically centered with the label.
 * @param props.testId                 The test identifier for component testing purposes.
 * @param props.validation             The validation function to validate the checkbox input value.
 * @returns The FormCheckboxInput component JSX element.
 *
 * @example
 * ```tsx
 * <FormCheckboxInput
 *     name="acceptTerms"
 *     initialValue={false}
 *     isRequired={true}
 *     validation={(value) => value ? null : 'You must accept the terms'}
 *     testId="terms-checkbox"
 * >
 *     I accept the terms and conditions
 * </FormCheckboxInput>
 * ```
 */
const FormCheckboxInput = observer(({
    children,
    className,
    initialValue,
    isDisabled,
    isRequired,
    name,
    onChange = () => {},
    selectionBoxIsCentered,
    testId = 'FormCheckboxInput',
    validation
}: WithChildren<ComponentProps>) => {
    const {colors, error, fieldRef, handleChange, value} = useFormCheckboxInput(
        name,
        initialValue,
        validation,
        onChange
    );

    return (
        <Wrapper className={className} data-cy={testId} variants={FadeInAnimation}>
            <Input
                ref={fieldRef}
                checked={value}
                disabled={isDisabled}
                id={name}
                required={isRequired}
                type="checkbox"
                onChange={handleChange}
            />
            <LabelElement
                $hasError={Boolean(error)}
                $isSelected={value}
                $selectionBoxIsCentered={selectionBoxIsCentered}
                htmlFor={name}
            >
                <P>
                    <Color $color={error ? colors.errorColor : 'inherit'}>
                        {children}
                    </Color>
                </P>
            </LabelElement>
        </Wrapper>
    );
});

FormCheckboxInput.displayName = 'FormCheckboxInput';

export {FormCheckboxInput};

const Wrapper = styled(motion.div)``;

const Input = styled.input`
    opacity: 0;
    position: absolute;
`;

interface LabelElementProps {
    $hasError?: boolean;
    $isSelected?: boolean;
    $selectionBoxIsCentered?: boolean;
}

const LabelElement = styled.label<LabelElementProps>`
    border: 0;
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    padding: ${spacing(0)};
    padding-inline-start: ${spacing(8)};
    position: relative;
    transition: border ease-in-out 0.25s;

    ${media('md')} {
        align-items: ${({$selectionBoxIsCentered}) => ($selectionBoxIsCentered ? 'center' : 'flex-start')};
        flex-direction: row;
    }

    &::after,
    &::before {
        background-color: ${({$hasError, theme}) => ($hasError ? `hsl(from ${theme.colors.errorColor} h s l / 0.3)` : 'transparent')};
        border: 1px solid ${({$hasError, theme}) => ($hasError ? theme.colors.errorColor : theme.colors.darkBorder)};
        border-radius: 0.5rem;
        content: "";
        display: block;
        height: 2.2rem;
        left: 0;
        position: absolute;
        top: 2px;
        transition: background-color 0.25s ease-in-out;
        width: 2.2rem;
    }

    &::after {
        border: 0;
        height: 2.2rem;
        left: 0;
        top: 2px;
        width: 2.2rem;
    }

    ${Input}:focus-visible + & {
        &::after,
        &::before {
            border-color: ${({theme}) => theme.colors.tertiary};
            outline: 1px solid hsl(from ${({theme}) => theme.colors.tertiary} H S L / 1);
        }
    }

    ${({$isSelected}) => $isSelected && `
        &::after {
            background-image: url(${iconAsBackground(Check, {color1: '#303030'})});
            background-size: 2.2rem;
            background-position: center;
            background-repeat: no-repeat;
        }
    `}
`;